import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DefaultPasswordRequirements from './DefaultPasswordRequirements';

const ESTIMATED_POPOVER_HEIGHT = 180;

/**
 * Floating popover for default-password requirements. Shown on field focus or while typing.
 * Portaled to document.body so it does not expand scrollable modal containers.
 */
const DefaultPasswordRequirementsPopover = ({ password, children, popoverClassName = '', placement = 'auto' }) => {
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = containerRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showAbove =
      placement === 'top' ||
      (placement === 'auto' && spaceBelow < ESTIMATED_POPOVER_HEIGHT && spaceAbove > spaceBelow);

    setPopoverStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 10000,
      ...(showAbove
        ? { bottom: viewportHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [placement]);

  const showPopover = useCallback(() => {
    setOpen(true);
  }, []);

  const hidePopover = useCallback(() => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (popoverRef.current?.contains(active) || containerRef.current?.contains(active)) {
        return;
      }
      setOpen(false);
    }, 0);
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPopoverStyle(null);
      return undefined;
    }

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition, password]);

  const popoverNode =
    open && popoverStyle
      ? createPortal(
          <div
            ref={popoverRef}
            role="tooltip"
            className={`rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg dark:border-gray-700 dark:bg-gray-800 ${popoverClassName}`}
            style={popoverStyle}
            onMouseDown={(event) => event.preventDefault()}
          >
            <DefaultPasswordRequirements
              password={password}
              showWhenEmpty
              itemClassName="text-[11px] leading-tight"
              helperClassName="text-[11px] leading-tight text-gray-500"
              className="mt-0 space-y-0.5"
            />
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={containerRef} onFocus={showPopover} onBlur={hidePopover}>
      {children}
      {popoverNode}
    </div>
  );
};

export default DefaultPasswordRequirementsPopover;
